import type { SupplyChainEntity } from "@/types/api";

/**
 * Mirror of `tideway_core::domain::supply_chain_entity::slugify`:
 * lowercase, collapse whitespace / punctuation to `_`, keep hyphens.
 */
export function slugifyPartyKey(raw: string): string {
  const trimmed = raw.trim().toLowerCase();
  let slug = "";
  let prevWasSep = false;
  for (const ch of trimmed) {
    if (/[a-z0-9-]/.test(ch)) {
      slug += ch;
      prevWasSep = false;
    } else if (!prevWasSep && slug.length > 0) {
      slug += "_";
      prevWasSep = true;
    }
  }
  return slug.replace(/_+$/, "");
}

export interface ResolvedFeedParty {
  /** Stable grouping key (canonical code when known). */
  canonical: string;
  /** Human label (entity display name when known). */
  label: string;
}

/**
 * Maps every known spelling of a supply-chain party — canonical code,
 * display name, aliases, and their slugs — onto one entity.
 */
export function buildFeedPartyAliasMap(
  entities: readonly SupplyChainEntity[],
): Map<string, ResolvedFeedParty> {
  const map = new Map<string, ResolvedFeedParty>();

  const index = (raw: string, resolved: ResolvedFeedParty) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    const keys = new Set([
      trimmed,
      trimmed.toLowerCase(),
      slugifyPartyKey(trimmed),
    ]);
    for (const key of keys) {
      if (key && !map.has(key)) {
        map.set(key, resolved);
      }
    }
  };

  for (const entity of entities) {
    const canonical = entity.canonical_code.trim();
    if (!canonical) continue;
    const label = entity.display_name.trim() || canonical;
    const resolved: ResolvedFeedParty = { canonical, label };
    index(canonical, resolved);
    index(entity.display_name, resolved);
    for (const alias of entity.aliases ?? []) {
      index(alias, resolved);
    }
  }

  return map;
}

/** Resolve a raw feed scope token through aliases, then slugify. */
export function resolveFeedPartyKey(
  raw: string,
  aliases?: Map<string, ResolvedFeedParty> | null,
): ResolvedFeedParty {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { canonical: trimmed, label: trimmed };
  }
  const hit =
    aliases?.get(trimmed) ??
    aliases?.get(trimmed.toLowerCase()) ??
    aliases?.get(slugifyPartyKey(trimmed));
  if (hit) return hit;
  const slug = slugifyPartyKey(trimmed);
  return {
    canonical: slug || trimmed,
    label: trimmed,
  };
}
